import { useEffect, useState } from 'react';
import { Modal, message } from 'antd';
import { PagesRoutes } from '../../../routing/PagesRoutes';
import { FollowUp, settings, shipment } from '../../../models/entities/shipment/shipment';
import { alertImgs, shipmentAlertsEnum, shipmentAlert } from '../../../models/entities/shipment/shipmentAlert';
import Overview from './sections/Overview';
import { styled } from '../../shared/Theme/theme';
import Button from '../../shared/SmallComponents/Button';
import EditIcon from '../../../static/icons/edit.svg';
import MenuIcon from '../../../static/icons/menu.svg';
import { Link } from 'react-router-dom';
import BackButton from '../../shared/SmallComponents/BackButton';
import ShipmentTabs, { ShipmentTabsPrintMode } from './sections/Tabs';
import DiscussionsSection from './sections/Discussions/DiscussionsSection';
import moment from 'moment';
import ShipmentMenu from '../../shared/Grid/specialColumns/ShipmentMenu';
import { TriggerProps } from '../../shared/Menu/Menu';
import { useNavigate } from 'react-router-dom';
import ShipmentFollowers from './components/shipmentFollowers';
import DropDown from '../../shared/inputs/base/DropDown';
import {
    UnifreightDocumentUploadSummary,
    UnifreightIntegrationResponse,
    UnifreightValidationError,
    UnifreightErrorResponse
} from '../../../models/entities/shipment/unifreightIntegration';
import UnifreightValidationErrorModal from '../../shared/UnifreightModals/UnifreightValidationErrorModal';
import UnifreightSuccessModal from '../../shared/UnifreightModals/UnifreightSuccessModal';
import renderStateOption from '../../shared/SmallComponents/StateOption';
import TimeLine, { TimeLineItem } from '../../shared/TimeLine/TimeLine';
import { settings as companySettings } from '../../../models/entities/businessPartner/businessPartner';
import { featureFlagsModel } from '../../../models/entities/businessPartner/settings';
import CZTooltip from '../../shared/CZTooltip/CZTooltip';
import ShipmentWarning from '../../shared/Grid/specialColumns/ShipmentWarning';
import ChooseForwarder, { stateProps } from '../../shared/ChooseForwarder';
import { chooseForwarderHandler } from '../../shared/ChooseForwarder/utils';
import claimsType from '../../../models/entities/claimsType';
import { bid } from '../../../models/entities/bid/bid';
import { drawers } from '../../../state/ducks/header/header';
import { claimsDomainsType } from '../../../userClaims';
import { purchaseOrderItem } from '../../../models/entities/purchaseOrder/purchaseOrderItem';
import { allowedCompany, userSettings } from '../../../models/entities/user';
import { css } from 'styled-components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Tooltip from '@mui/material/Tooltip';
import { userDetails } from '../../../state/ducks/users/users';
import OperatorTrackingSection from './sections/Events/OperatorTracking/OperatorTrackingSection';
import { isAllowedSubdomain } from '../../../utils/utils-functions';
import { eShipmentTypes } from '../../../models/entities/shipment/shipmentTypes';

type Props = {
    shipment: shipment;
    shipmentTags: Array<string>;
    onTagRemoveClicked: (tagName: string) => void;
    updateShipmentState: (shipmentId: string, shipmentState: string, forwarderId?: string) => Promise<void>;
    updateShipmentAccountingState: (shipmentId: string, shipmentAccountingState: string) => Promise<void>;
    dismissShipmentAlert: (shipmentId: string, alert: shipmentAlert) => Promise<void>;
    // SAP integration is disabled for now
    // fetchSendToSapGUID: (guid: string) => Promise<void>;
    fetchSendToSapGUID?: (guid: string) => Promise<void>;
    sendToUnifreight: (shipmentId: string) => Promise<UnifreightIntegrationResponse>;
    shipmentPoItems?: Array<purchaseOrderItem>;
    isLoadingShipment: boolean;
    historyUrls: string[];
    isDrawerOpen?: boolean;
    shipmentState?: string;
    shipmentStateTimelineData?: Array<TimeLineItem>;
    localization?: any;
    settings: settings;
    companySettings?: companySettings;
    printMode?: boolean;
    allowedCompanies: allowedCompany[];
    getBPRemarksById: (id: string) => string;
    getBPRemarksAlertById: (id: string) => boolean;
    dismissRequiredDoc: (shipmentId: string, docType: string) => string;
    getUserClaims: (domain: claimsDomainsType) => claimsType;
    isFreelancerOrForwarder?: boolean;
    reStateShipmentById: (shipmentId: string) => Promise<shipment>;
    updateBid?: (id: string, bid: bid, shipment: shipment, publish: boolean) => Promise<boolean>;
    createBid: (shipment: shipment, bid: bid, publish: boolean) => Promise<string>;
    startBid?: (id: string, shipmentId: string, shipment: shipment) => Promise<void>;
    featureFlags?: featureFlagsModel;
    isAllowedShipment?: boolean;
    setDrawerOpen: (open: drawers, params?: any) => void;
    addNewFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    updateFollowUpCompletionStatus: (shipmentId: string, followUpId: number, isCompleted: boolean) => Promise<void>;
    updateFollowUp: (shipmentId: string, followUp: FollowUp) => Promise<void>;
    deleteFollowUp: (shipmentId: string, followUpId: number) => Promise<void>;
    users: userDetails[];
    followupGridSettings: userSettings;
    currentUserId: string;
    onRefreshShipment?: () => void;
    cargoOwnerProfitSurchargePercent?: number | null;
    cargoOwnerBaseCurrency?: string | null;
    cargoOwnerVat?: number | null;
};

const SingleShipmentPage = ({
    shipment,
    shipmentTags,
    onTagRemoveClicked,
    updateShipmentState,
    updateShipmentAccountingState,
    dismissShipmentAlert,
    getBPRemarksById,
    getBPRemarksAlertById,
    isLoadingShipment,
    historyUrls,
    isDrawerOpen,
    shipmentState,
    shipmentStateTimelineData,
    localization,
    settings,
    companySettings,
    printMode,
    getUserClaims,
    reStateShipmentById,
    dismissRequiredDoc,
    isFreelancerOrForwarder,
    updateBid,
    createBid,
    startBid,
    featureFlags,
    setDrawerOpen,
    isAllowedShipment,
    // SAP integration is disabled for now
    // fetchSendToSapGUID,
    sendToUnifreight,
    addNewFollowUp,
    deleteFollowUp,
    updateFollowUpCompletionStatus,
    users,
    currentUserId,
    updateFollowUp,
    followupGridSettings,
    onRefreshShipment,
    cargoOwnerProfitSurchargePercent,
    cargoOwnerBaseCurrency,
    cargoOwnerVat
}: Props) => {
    const navigate = useNavigate();
    const [chooseForwarder, setChooseForwarder] = useState<stateProps>();
    const [timeLineOpen, setTimeLineOpen] = useState<boolean>(true);
    const [isBPPopupVisible, setIsBPPopupVisible] = useState(false);
    const [isAlertedClient, setIsAlertedClient] = useState<boolean>(false);
    const [isFedExTrackingModalOpen, setIsFedExTrackingModalOpen] = useState(false);

    // Unifreight integration state
    const [isUnifreightLoading, setIsUnifreightLoading] = useState(false);
    const [showValidationErrorModal, setShowValidationErrorModal] = useState(false);
    const [validationErrors, setValidationErrors] = useState<UnifreightValidationError[]>([]);
    const [validationErrorCount, setValidationErrorCount] = useState(0);
    const [validationErrorMessage, setValidationErrorMessage] = useState('');
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successFileName, setSuccessFileName] = useState('');
    const [successShipmentNumber, setSuccessShipmentNumber] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [validationSkipped, setValidationSkipped] = useState(false);
    const [documentsSummary, setDocumentsSummary] = useState<UnifreightDocumentUploadSummary | undefined>(undefined);

    // Check for Send to UNF permission
    const hasSendToUnfPermission = !!getUserClaims('shipment').send_to_unf;
    // Check if current subdomain is allowed (orner or omrisforwarding)
    const isAllowedUnifreightSubdomain = isAllowedSubdomain(['orner', 'omrisforwarding']);

    useEffect(() => {
        if (shipment.cargoOwnerId) {
            const timer = setTimeout(() => {
                const alertStatus = getBPRemarksAlertById(shipment.cargoOwnerId || '');
                setIsAlertedClient(!!alertStatus);
            }, 100);
            return () => clearTimeout(timer);
        } else {
            setIsAlertedClient(false);
        }
    }, [getBPRemarksAlertById]);

    useEffect(() => {
        if (isAlertedClient) {
            setIsBPPopupVisible(true);
        }
    }, [isAlertedClient]);

    const toggleTimeLine = () => {
        setTimeLineOpen(!timeLineOpen);
    };

    const backPath = ((): string => {
        let backPath = PagesRoutes.Shipments;
        const lastFiveUrls = historyUrls.slice(0, 5);

        for (let i = 0; i < lastFiveUrls.length; i++) {
            const url = lastFiveUrls[i];

            if (!(url?.includes('/shipment/edit') || url === lastFiveUrls[0])) {
                backPath = url;
                break;
            }
        }

        return backPath;
    })();

    const dismissAlert = async (alert: shipmentAlert) => {
        Modal.confirm({
            content: <div>{localization.are_you_sure}</div>,
            async onOk() {
                await dismissShipmentAlert(shipment.id, alert);
            },
            onCancel() {}
        });
    };
    const renderShipmentAlerts = () => {
        return shipment.alerts
            ?.sort((a, b) => a.level - b.level)
            .map((shipmentAlert: shipmentAlert, i: number) => {
                if (!shipmentAlert.isDismissed) {
                    return (
                        <AlertItem key={i}>
                            <AlertImage
                                src={alertImgs[shipmentAlert.level]}
                                alt={localization.alert_status[shipmentAlertsEnum[shipmentAlert.level]]}
                            />
                            <AlertMessage>{shipmentAlert.message}</AlertMessage>
                            <CZTooltip text={localization.dismiss}>
                                <span className="material-icons" onClick={() => dismissAlert(shipmentAlert)}>
                                    close
                                </span>
                            </CZTooltip>
                        </AlertItem>
                    );
                }
            });
    };

    const handleOk = () => {
        setIsBPPopupVisible(false);
    };
    const handleCancel = () => {
        setIsBPPopupVisible(false);
    };

    // SAP integration is disabled for now
    // // SAP Integration Handler
    // const handleSapIntegration = async () => {
    //     if (!shipment.cargoOwnerId) {
    //         alert('Shipment is not related to cargo owner. Complete the data and try again.');
    //         return;
    //     }

    //     try {
    //         await fetchSendToSapGUID(shipment.id);
    //         alert('Shipment has been sent to SAP successfully');
    //     } catch (error) {
    //         alert('Failed to send shipment to SAP. Please try again.');
    //     }
    // };

    // Unifreight Integration Handler
    const handleUnifreightIntegration = async () => {
        if (isUnifreightLoading) return; // Prevent multiple clicks

        // Check if shipment type is supported for Unifreight integration
        const unsupportedTypes = ['CUSTOMS', 'INLAND', 'RAIL'];
        if (unsupportedTypes.includes(shipment.typeCode)) {
            const shipmentTypeName = eShipmentTypes[shipment.typeCode] || shipment.typeCode;
            const supportedTypes = ['Ocean Export', 'Ocean Import', 'Ocean Express', 'Air Export', 'Air Import', 'Express', 'Drop Ocean', 'Drop Air'];
            Modal.error({
                title: 'Unsupported Shipment Type',
                content: (
                    <div>
                        <p style={{ marginBottom: '12px', color: '#ff4d4f' }}>
                            <strong>{shipmentTypeName} shipments are not currently supported for Unifreight integration.</strong>
                        </p>
                    </div>
                ),
                zIndex: 1011
            });
            return;
        }

        setIsUnifreightLoading(true);
        try {
            const result = await sendToUnifreight(shipment.id);

            if (result.success) {
                // Show success modal
                setSuccessFileName(result.fileName);
                setSuccessShipmentNumber(result.shipmentNumber);
                setSuccessMessage(result.message);
                setValidationSkipped(result.validationSkipped);
                setDocumentsSummary(result.documents);
                setShowSuccessModal(true);
            } else {
                // Show validation error modal
                const errorResult = result as UnifreightErrorResponse;
                setValidationErrors(errorResult.validationErrors);
                setValidationErrorCount(errorResult.errorCount);
                setValidationErrorMessage(errorResult.message);
                setShowValidationErrorModal(true);
            }
        } catch (error: any) {
            // Handle unexpected errors
            alert(error?.message || 'Failed to send shipment to Unifreight. Please try again.');
        } finally {
            setIsUnifreightLoading(false);
        }
    };

    const getPlainTextFromHTML = (htmlString: string) => {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlString;
        return tempDiv.textContent || tempDiv.innerText || '';
    };
    return (
        <PageWrapper>
            <Modal
                title="Client Remarks:"
                open={isBPPopupVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                cancelButtonProps={{ style: { display: 'none' } }}
                okText="Confirm"
                zIndex={1005}
                mask={true}
                maskClosable={true}
            >
                <div>{getPlainTextFromHTML(getBPRemarksById(shipment.cargoOwnerId || ''))}</div>
            </Modal>

            {chooseForwarder && (
                <ChooseForwarder
                    companyId={chooseForwarder.companyId}
                    onSubmit={chooseForwarder.func}
                    submitArgs={chooseForwarder.args}
                    closeModal={() => setChooseForwarder(undefined)}
                />
            )}
            <LeftSide isTimeLineOpen={timeLineOpen} isDrawerOpen={isDrawerOpen} printMode={printMode} id="single-shipment-page-left-container">
                {!printMode && (
                    <SectionWrapper>
                        <div>
                            <BackButton style={{ marginLeft: '-30px' }} toPath={backPath} />
                            <LastUpdateText>
                                {localization.lastUpdate} {moment(shipment.updatedAt).format('DD/MM/YYYY HH:mm')}
                            </LastUpdateText>
                        </div>
                        <ShipmentStateDropDownContainer>
                            <DropDown
                                placeHolder={localization.shipment_state}
                                style={{ width: '100%' }}
                                options={
                                    settings?.state
                                        ?.sort((a, b) => a.displayOrder - b.displayOrder)
                                        .map((item) => ({
                                            text: item.name,
                                            value: item.code,
                                            color: item.color
                                        })) || []
                                }
                                value={shipmentState}
                                optionRenderer={renderStateOption}
                                onChange={async (value: string) => {
                                    await chooseForwarderHandler(
                                        shipment,
                                        value,
                                        setChooseForwarder,
                                        updateShipmentState,
                                        isFreelancerOrForwarder,
                                        settings
                                    );
                                }}
                                disabled={featureFlags?.UNDER_FORWARDER || !getUserClaims('shipment').edit_state}
                                verifyChange={{
                                    title: (value: string) => `${localization.you_are_moving_the_shipment_state_to} ${
                                        settings?.state?.find((item) => item.code === value)?.name
                                    }.
                                ${localization.are_you_sure}`
                                }}
                            />
                        </ShipmentStateDropDownContainer>
                        <ShipmentWarningContainer>
                            <ShipmentWarning alertStatus={shipment.alertStatus} shipmentId={shipment.id} />
                        </ShipmentWarningContainer>
                        {!shipment.isActive && <InactiveBadge>{localization.inactive}</InactiveBadge>}
                        {shipment.deletedAt && <InactiveBadge>{localization.deleted}</InactiveBadge>}
                        <div style={{ flex: 1 }}></div>
                        <HeaderRightSide>
                            <ShipmentFollowers followers={shipment.followers} />
                            {!featureFlags?.UNDER_FORWARDER && (
                                <Link
                                    to={{
                                        pathname: `${PagesRoutes.EditShipment}/${shipment.id}`
                                    }}
                                >
                                    <Button margin="0 12px 0 0">
                                        <img src={EditIcon} alt={localization.edit} />
                                        <span>{localization.edit}</span>
                                    </Button>
                                </Link>
                            )}

                            {!featureFlags?.UNDER_FORWARDER && isAllowedShipment && getUserClaims('shipment').duplicate_shipment && (
                                <Link
                                    to={{
                                        pathname: `${PagesRoutes.DuplicateShipment}/${shipment.id}`
                                    }}
                                >
                                    <Button margin="0 12px 0 0">
                                        <span>{localization.duplicate}</span>
                                    </Button>
                                </Link>
                            )}

                            {/* Integration Buttons */}
                            {/* SAP integration is disabled for now */}
                            {/* {featureFlags?.SAP_INTEGRATION && (
                                <Button
                                    width="auto"
                                    padding="8px 16px"
                                    margin="0 12px 0 0"
                                    minHeight="30px"
                                    onClick={handleSapIntegration}
                                >
                                    <span style={{ whiteSpace: 'nowrap' }}>{localization.SendToSAP}</span>
                                </Button>
                            )} */}

                            {hasSendToUnfPermission && isAllowedUnifreightSubdomain && (
                                <Button
                                    width="auto"
                                    padding="8px 16px"
                                    margin="0 12px 0 0"
                                    minHeight="30px"
                                    onClick={handleUnifreightIntegration}
                                    disabled={isUnifreightLoading}
                                >
                                    <span style={{ whiteSpace: 'nowrap' }}>
                                        {isUnifreightLoading ? (localization.unifreight_sending || 'Sending to Unifreight...') : 'Send to UNF'}
                                    </span>
                                </Button>
                            )}

                            {(shipment.operator === 'FEDEX' || shipment.carrierCode === 'FEDEX') && (
                                <Button
                                    width="auto"
                                    padding="8px 16px"
                                    margin="0 12px 0 0"
                                    minHeight="30px"
                                    onClick={() => setIsFedExTrackingModalOpen(true)}
                                >
                                    <span style={{ whiteSpace: 'nowrap' }}>FedEx Tracking</span>
                                </Button>
                            )}

                            <Button
                                width="auto"
                                height="auto"
                                buttonType="hollow"
                                padding="0 8px"
                                margin="0 10px 0 0"
                                borderRadiusMobile="50%"
                                minHeightMobile="32px"
                                paddingMobile="0 4px"
                                widthMobile="auto"
                                onClick={() => reStateShipmentById(shipment.id)}
                            >
                                <span className="material-icons">refresh</span>
                            </Button>
                            <ShipmentMenu
                                shipment={shipment}
                                entityId={shipment.id}
                                entityData={{ CargoZoneNumber: shipment.CargoZoneNumber, companyId: shipment.companyId }}
                                trigger={(props: TriggerProps) => (
                                    <img style={{ cursor: 'pointer' }} onClick={(e) => props.onClick(e)} src={MenuIcon} alt="Menu" />
                                )}
                                onDelete={() => navigate(PagesRoutes.Shipments)}
                            />
                        </HeaderRightSide>
                    </SectionWrapper>
                )}
                <OverviewSectionWrapper>
                    <Overview
                        shipment={shipment}
                        tags={shipmentTags}
                        onTagRemoveClicked={onTagRemoveClicked}
                        localization={localization}
                        settings={settings}
                        printMode={printMode}
                        featureFlags={featureFlags}
                        isFreelancerOrForwarder={isFreelancerOrForwarder}
                        isAllowedShipment={isAllowedShipment}
                    />
                </OverviewSectionWrapper>
                <SectionWrapper>
                    {!printMode ? (
                        <ShipmentTabs
                            shipment={shipment}
                            localization={localization}
                            settings={settings}
                            companySettings={companySettings}
                            isLoadingShipment={isLoadingShipment}
                            getUserClaims={getUserClaims}
                            isAllowedShipment={isAllowedShipment}
                            updateShipmentAccountingState={updateShipmentAccountingState}
                            dismissRequiredDoc={dismissRequiredDoc}
                            updateBid={updateBid}
                            createBid={createBid}
                            startBid={startBid}
                            featureFlags={featureFlags}
                            setDrawerOpen={setDrawerOpen}
                            isFreelancerOrForwarder={isFreelancerOrForwarder}
                            addNewFollowUp={addNewFollowUp}
                            updateFollowUpCompletionStatus={updateFollowUpCompletionStatus}
                            deleteFollowUp={deleteFollowUp}
                            users={users}
                            currentUserId={currentUserId}
                            updateFollowUp={updateFollowUp}
                            followupGridSettings={followupGridSettings}
                            onRefreshShipment={onRefreshShipment}
                            cargoOwnerProfitSurchargePercent={cargoOwnerProfitSurchargePercent}
                            cargoOwnerBaseCurrency={cargoOwnerBaseCurrency}
                            cargoOwnerVat={cargoOwnerVat}
                        />
                    ) : (
                        <ShipmentTabsPrintMode
                            shipment={shipment}
                            localization={localization}
                            settings={settings}
                            isAllowedShipment={isAllowedShipment}
                            companySettings={companySettings}
                            printMode={printMode}
                        />
                    )}
                </SectionWrapper>
                {!printMode && (
                    <SectionWrapper>
                        <DiscussionsSection localization={localization} />
                    </SectionWrapper>
                )}
            </LeftSide>
            <RightSide isTimeLineOpen={timeLineOpen}>
                <ToggleBtn onClick={toggleTimeLine}>{!timeLineOpen ? <ChevronLeftIcon /> : <ChevronRightIcon />}</ToggleBtn>

                <RightSideContent>
                    <AlertsListContainer>{renderShipmentAlerts()}</AlertsListContainer>
                    <TimeLineContainer>
                        <TimeLine data={shipmentStateTimelineData || []} />
                    </TimeLineContainer>
                </RightSideContent>
            </RightSide>
            
            {/* FedEx Tracking Modal */}
            {isFedExTrackingModalOpen && (
                <OperatorTrackingSection
                    key={`fedex-modal-${shipment.id}-${shipment.updatedAt || shipment.createdAt}`}
                    shipment={shipment}
                    onClose={() => {
                        setIsFedExTrackingModalOpen(false);
                    }}
                    onRefreshComplete={() => {
                        setIsFedExTrackingModalOpen(false);
                        onRefreshShipment();
                    }}
                />
            )}

            {/* Unifreight Validation Error Modal */}
            <UnifreightValidationErrorModal
                visible={showValidationErrorModal}
                errors={validationErrors}
                errorCount={validationErrorCount}
                message={validationErrorMessage}
                onClose={() => setShowValidationErrorModal(false)}
                localization={localization}
            />

            {/* Unifreight Success Modal */}
            <UnifreightSuccessModal
                visible={showSuccessModal}
                fileName={successFileName}
                shipmentNumber={successShipmentNumber}
                message={successMessage}
                validationSkipped={validationSkipped}
                documents={documentsSummary}
                onClose={() => setShowSuccessModal(false)}
                localization={localization}
            />
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
    display: flex;
    flex-direction: row;
    height: 100%;
    position: relative;

    @media screen and (max-width: 768px) {
        height: calc(100vh - 65px - 40px);
    }
`;

const TimeLineContainer = styled.div`
    min-width: 380px;
    padding: 20px 36px 10px 7px;
`;

const RightSideContent = styled.div`
    min-width: 380px;
    overflow-y: auto;
    height: 100%;
`;

const ToggleBtn = styled.div`
    position: absolute;
    top: 9px;
    left: -24px;
    width: 24px;
    color: #1e98cf;
    border: 1px solid #1e98cf;
    background-color: #eff8fc;
    border-right: none;
    height: 32px;
    border-radius: 10px 0px 0px 10px;
    padding: 3px 0;
    transition: 100ms ease-in-out;
    cursor: pointer;
    &:hover {
        box-shadow: 0 3px 5px 0 rgba(0, 0, 0, 0.2);
        width: 34px;
        left: -34px;
    }
`;

const RightSide = styled.div<{ isTimeLineOpen: boolean }>`
    position: fixed;
    z-index: 1002;
    top: 64px;
    right: 70px;
    transition: 225ms ease-in-out;
    ${(props) =>
        props.isTimeLineOpen
            ? css`
                  width: 380px;
                  max-width: 380px;
              `
            : css`
                  width: 0px;
                  max-width: 0px;
              `}
    height: calc(100vh - 64px);
    overflow: visible;
    @media screen and (max-width: 1300px) {
        display: none;
    }
`;
const ShipmentStateDropDownContainer = styled.div`
    align-self: flex-end;
    width: 25%;
    margin-left: 20px;
`;
const ShipmentWarningContainer = styled.div`
    align-self: flex-end;
    margin-left: 10px;
    height: 25px;
`;

const AlertsListContainer = styled.div`
    min-width: 380px;
    flex: 1;
    width: 100%;
    display: flex;
    flex-direction: column;
    border-bottom: solid 1px #e0e0e0;

    & :last-child {
        margin-bottom: 0;
    }
`;
export const AlertItem = styled.div`
    display: flex;
    flex-direction: row;
    padding: 10px 36px 10px 34px;
    align-items: center;
    transition: 0.1s;
    &:hover {
        background-color: #f1f1f1;
    }
    .material-icons {
        margin-left: auto;
        font-size: 12px;
        text-align: right;
        color: #a2a2a2;
        cursor: pointer;
        transition: 0.1s;

        &:hover {
            color: black;
        }
    }
`;
export const AlertMessage = styled.div`
    color: #394372;
`;
export const AlertImage = styled.img`
    margin-right: 8px;
    height: 18px;
`;

const LeftSide = styled.div<{ isTimeLineOpen: boolean; isDrawerOpen?: boolean; printMode?: boolean }>`
    ${(props) =>
        props.isDrawerOpen
            ? ``
            : css`
                  transition: 225ms ease-in-out;
              `}
    width: ${(props) => (props.isDrawerOpen || props.printMode || !props.isTimeLineOpen ? '100%' : 'calc(100% - 380px)')};
    max-width: ${(props) => (props.isDrawerOpen || props.printMode || !props.isTimeLineOpen ? '100%' : 'calc(100% - 380px)')};
    border-right: 1px solid ${(props) => props.theme.colors.grayDefaultBorder};
    overflow-y: overlay;
    // margin-top: 64px;
    // padding-right: ${(props) => (props.isDrawerOpen || props.printMode || !props.isTimeLineOpen ? '70px' : '0')};
    @media screen and (max-width: 1300px) {
        width: 100%;
        max-width: 100%;
    }
`;

const OverviewSectionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    border-bottom: solid 1px ${(props) => props.theme.colors.grayDefaultBorder};
    justify-content: space-between;
`;

export const SectionWrapper = styled.div`
    display: flex;
    flex-direction: row;
    padding: 24px 18px 18px 48px;
    border-bottom: solid 1px ${(props) => props.theme.colors.grayDefaultBorder};
    justify-content: space-between;
`;

const HeaderRightSide = styled.div`
    display: flex;
    height: 30px;
    margin: 30px 0 0 0;
`;

const InactiveBadge = styled.span`
    border-radius: 2vh;
    border: 1px solid #ff7676;
    color: #ff7676;
    font-size: 14px;
    height: 24px;
    padding: 3px 8px;
    margin-left: 10px;
    display: inline-flex;
    align-items: center;
    line-height: 1px;
    align-self: flex-end;
    min-width: max-content;
`;

const LastUpdateText = styled.div`
    font-size: 12px;
    font-style: italic;
    color: #757575;
    padding: 16px 0 0;
`;

export default SingleShipmentPage;
